import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Hotel, MapPin, Check, ArrowRight, Filter, Search, Star, X, SlidersHorizontal, ChevronDown, ChevronUp, Sparkles, ShieldCheck } from 'lucide-react';
import { hotels, destinations } from '../data';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { hasPoolLift, isAdultsOnly, isChildFriendly, isApartment, isWellness, matchesHotelSearch } from '../lib/hotelFilters';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../lib/schema';

const HotelCard = ({ hotel: rawHotel, index, dest: rawDest }) => {
  const containerRef = useRef(null);
  const [hasSpace, setHasSpace] = useState(false);
  const { language, t, translateObject, getLocalizedPath } = useLanguage();

  const hotel = translateObject(rawHotel);
  const dest = translateObject(rawDest);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setHasSpace(entry.contentRect.width >= 379);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.3) }}
    >
      <Link 
        to={getLocalizedPath('hotel', language, { hotelId: hotel.id })}
        className="group block bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-emerald-50"
      >
        <div className="flex flex-col md:flex-row h-full w-full min-w-0">
          <div ref={containerRef} className="md:w-5/12 lg:w-5/12 xl:w-[42%] relative h-72 sm:h-84 md:h-auto md:min-h-[340px] overflow-hidden shrink-0">
            <img 
              src={hotel.image} 
              alt={hotel.name} 
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            {hotel.isNew && (
              <div className="absolute top-4 left-4 z-20 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white font-black text-xs uppercase px-3.5 py-1.5 rounded-full shadow-lg tracking-widest flex items-center gap-1.5 border border-amber-200/50 animate-pulse">
                <Sparkles size={13} className="fill-white shrink-0" />
                {language === 'da' ? 'NYHED' : 'NEW'}
              </div>
            )}
            <div className={`absolute ${hotel.isNew ? 'top-14 left-4' : 'top-4 left-4'} z-10 bg-white/90 backdrop-blur-sm px-3.5 py-1.5 rounded-2xl text-xs font-bold text-emerald-900 shadow-sm flex items-start gap-1.5 max-w-[calc(100%-2rem)]`}>
              <MapPin size={12} className="shrink-0 mt-0.5 text-emerald-800" />
              <div className="flex flex-col leading-tight">
                <span className="uppercase tracking-wider text-[11px] font-extrabold text-emerald-950">{hotel.area}</span>
                {dest?.title && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800/80">{dest.title}</span>
                )}
              </div>
            </div>

            {hotel.imageBadge && (
              <div className="absolute top-4 right-4 z-20 bg-emerald-950/85 text-white backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider shadow-lg border border-emerald-500/40">
                {hotel.imageBadge}
              </div>
            )}
          </div>
          <div className="md:w-7/12 lg:w-7/12 xl:w-[58%] p-6 sm:p-8 flex flex-col justify-between min-w-0">
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                <div className="min-w-0 flex-1">
                  <div className="flex gap-1 mb-2">
                    {[...Array(hotel.stars || 0)].map((_, i) => <Star key={i} size={14} className="fill-emerald-600 text-emerald-600" />)}
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-serif font-bold text-emerald-900 group-hover:text-emerald-700 transition-colors italic break-words">
                    {hotel.name}
                  </h3>
                </div>
                <div className="bg-emerald-50/60 border border-emerald-100/80 px-5 py-3.5 rounded-2xl text-right flex flex-col items-end justify-center shrink-0 sm:min-w-[140px] shadow-sm">
                  <p className="text-2xl font-sans font-extrabold text-emerald-950 leading-none">
                    <span className="text-sm font-normal text-emerald-700 mr-1.5">{language === 'da' ? 'Fra' : 'From'}</span>
                    {hotel.priceFrom.toLocaleString(language === 'da' ? 'da-DK' : 'en-US')},-
                  </p>
                  <p className="text-[10px] text-emerald-700 font-bold uppercase mt-1.5">
                    {language === 'da' ? 'pr. pers.' : 'per person'}
                  </p>
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed mb-4 line-clamp-2 md:line-clamp-none max-w-2xl break-words [overflow-wrap:anywhere]">
                {hotel.description}
              </p>
              <p className="text-[10px] text-slate-400 italic mb-6 leading-relaxed">
                {language === 'da' 
                  ? '* Prisen er en fra-pris og afhænger blandt andet af afrejsedato, rejseperiode, værelsestype, ledige pladser og eventuelle særlige behov. Kontakt os for et konkret tilbud på din rejse.'
                  : '* The price is a starting price and depends, among other things, on departure date, travel period, room type, availability, and any special needs. Contact us for a specific quote for your trip.'
                }
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {hotel.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50/50 px-3 py-1.5 rounded-lg">
                    <Check size={14} className="text-emerald-600" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-stone-100">
              <div className="hidden sm:flex items-center gap-4 text-xs font-bold text-slate-400 uppercase tracking-widest">
                <span>WiFi</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <span>{language === 'da' ? 'Elevator' : 'Elevator'}</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <span>{language === 'da' ? 'Aircon' : 'Air Conditioning'}</span>
              </div>
              <div className="flex items-center gap-2 text-emerald-900 font-bold group-hover:gap-4 transition-all">
                {language === 'da' ? 'Se hoteloplysninger' : 'View Hotel Details'}
                <ArrowRight size={20} className="text-emerald-600" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const AllHotelsPage = () => {
  const { language, t, translateObject, getLocalizedPath } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  
  const selectedDestination = searchParams.get('destination') || 'all';
  const setSelectedDestination = (val) => {
    const newParams = new URLSearchParams(searchParams);
    if (val === 'all') {
      newParams.delete('destination');
    } else {
      newParams.set('destination', val);
    }
    setSearchParams(newParams);
  };

  const [filterPoolLift, setFilterPoolLift] = useState(false);
  const [filterWellness, setFilterWellness] = useState(false);
  const [filterBoernevenlig, setFilterBoernevenlig] = useState(false);
  const [filterLejligheder, setFilterLejligheder] = useState(false);
  const [filterVoksenhotel, setFilterVoksenhotel] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Collapsable sidebar sections
  const [isDestinationsOpen, setIsDestinationsOpen] = useState(false);
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredHotels = hotels.filter(rawHotel => {
    const hotel = translateObject(rawHotel);
    const dest = destinations.find(d => d.id === hotel.destinationId);
    const translatedDest = translateObject(dest);
    const destTitle = translatedDest ? translatedDest.title : '';
    
    // Search matches hotel name, area, destination title, features, amenities, or descriptions
    const matchesSearch = matchesHotelSearch(hotel, searchTerm, destTitle);

    const matchesDestination = selectedDestination === 'all' || hotel.destinationId === selectedDestination;
    
    // Checkbox filters
    const matchesPoolLift = !filterPoolLift || hasPoolLift(hotel);
    const matchesWellness = !filterWellness || isWellness(hotel);
    const matchesBoernevenlig = !filterBoernevenlig || isChildFriendly(hotel);
    const matchesLejligheder = !filterLejligheder || isApartment(hotel);
    const matchesVoksenhotel = !filterVoksenhotel || isAdultsOnly(hotel);

    return matchesSearch && matchesDestination && matchesPoolLift && matchesWellness && matchesBoernevenlig && matchesLejligheder && matchesVoksenhotel;
  });

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedDestination('all');
    setFilterPoolLift(false);
    setFilterWellness(false);
    setFilterBoernevenlig(false);
    setFilterLejligheder(false);
    setFilterVoksenhotel(false);
  };

  const isAnyFilterActive = searchTerm !== '' || selectedDestination !== 'all' || filterPoolLift || filterWellness || filterBoernevenlig || filterLejligheder || filterVoksenhotel;

  const breadcrumbs = [
    {
      name: language === 'da' ? 'Forside' : 'Home',
      url: getLocalizedPath('home', language)
    },
    {
      name: language === 'da' ? 'Handicapvenlige hoteller' : 'Accessible hotels',
      url: getLocalizedPath('hotels', language)
    }
  ];
  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs);
  const organizationSchema = buildOrganizationSchema({ language });

  return (
    <div className="bg-stone-50 min-h-screen pb-20 relative">
      <SEO 
        title={language === 'da' ? "Handicapvenlige hoteller | Indhent tilbud på dit næste ophold" : "Accessible Hotels | Request a Quote for Your Next Stay"} 
        description={language === 'da' 
          ? "Find handicapvenlige hoteller i Europa. Få personlig rådgivning om tilgængelighed, hotelvalg og et uforpligtende tilbud tilpasset dine behov."
          : "Find accessible hotels in Europe. Get personal advice on accessibility, hotel selection, and a non-binding quote tailored to your needs."
        }
        ogImage="https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/ceef4750-2ca2-43c1-b82b-e06ba062b589.jpeg"
        schemaMarkup={[organizationSchema, breadcrumbSchema]}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      {/* Hero Section */}
      <div className="relative h-[35vh] md:h-[45vh] overflow-hidden">
        <img 
          src="/images/hotel-pool-accessible.jpg" 
          alt={language === 'da' ? "Alle Hoteller" : "All Hotels"} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center pt-20 md:pt-24">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4 italic">
              {language === 'da' ? 'Handicapvenlige hoteller' : 'Accessible Hotels'}
            </h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto font-light">
              {language === 'da' 
                ? 'Håndplukkede hoteller med fokus på tilgængelighed, komfort og den gode ferieoplevelse.'
                : 'Handpicked hotels with a focus on accessibility, comfort, and a great holiday experience.'
              }
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-12 relative z-10">
        
        {/* Intro Text */}
        <div className="w-full mb-6 bg-stone-50/80 backdrop-blur-sm p-6 md:p-8 rounded-3xl border border-stone-200/60 shadow-sm">
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-emerald-900 italic mb-2">
              {language === 'da' ? 'Handicapvenlige hoteller til en tryg rejseoplevelse' : 'Accessible Hotels for a Safe Travel Experience'}
            </h2>
            <p className="text-stone-700 text-sm md:text-base leading-relaxed">
              {language === 'da'
                ? 'At finde handicapvenlige hoteller bør ikke være et spørgsmål om held. Når du rejser med kørestol eller har nedsat mobilitet, er det vigtigt at vide, hvordan forholdene faktisk er på hotellet og destinationen.'
                : 'Finding accessible hotels should not be a matter of luck. When you travel with a wheelchair or have reduced mobility, it is important to know what the conditions are actually like at the hotel and destination.'
              }
            </p>
            <div className="text-stone-700 space-y-4 leading-relaxed text-sm md:text-base">
              <p>
                {language === 'da' ? (
                  <>
                    Hos <Link to={getLocalizedPath('home', language)} className="text-[#99A18C] hover:underline font-semibold">Alora Rejser</Link> hjælper vi kørestolsbrugere og gangbesværede med at finde handicapvenlige hoteller, hvor tilgængelighed, komfort og tryghed er tænkt ind fra starten. Vi hjælper med at afklare de praktiske detaljer, så du kan fokusere på at nyde ferien.
                  </>
                ) : (
                  <>
                    At <Link to={getLocalizedPath('home', language)} className="text-[#99A18C] hover:underline font-semibold">Alora Rejser</Link> we help wheelchair users and people with walking difficulties find accessible hotels where accessibility, comfort, and safety are built-in from the start. We help clarify the practical details so you can focus on enjoying your holiday.
                  </>
                )}
              </p>
              <p>
                {language === 'da'
                  ? 'Her finder du handicapvenlige hoteller på udvalgte destinationer som Mallorca, Kreta, Venedig, Paris og Østrig. Hvert hotel er udvalgt med fokus på gode adgangsforhold og en ferieoplevelse, der fungerer i praksis.'
                  : 'Here you will find accessible hotels in selected destinations such as Mallorca, Crete, Venice, Paris, and Austria. Each hotel is selected with a focus on good access conditions and a holiday experience that works in practice.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Pricing & Safety Package Callout */}
        <div className="w-full mb-8 bg-[#99A18C]/10 border border-[#99A18C]/25 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-full bg-[#99A18C]/20 flex items-center justify-center text-[#99A18C] shrink-0">
              <ShieldCheck size={22} />
            </div>
            <div>
              <p className="text-sm font-serif font-bold text-emerald-950">
                {language === 'da' ? 'Gennemskuelige priser & Aloras Tryghedspakke' : 'Transparent Pricing & Alora Safety Package'}
              </p>
              <p className="text-xs text-stone-600">
                {language === 'da' 
                  ? 'Ingen skjulte gebyrer. Vi inkluderer altid nødvendig assistance og tilgængelige rammer.' 
                  : 'No hidden fees. We always include necessary assistance and accessible settings.'}
              </p>
            </div>
          </div>
          <Link
            to={getLocalizedPath('prices', language)}
            className="whitespace-nowrap text-xs font-bold text-[#99A18C] hover:text-emerald-950 bg-white border border-[#99A18C]/30 px-4 py-2.5 rounded-xl hover:bg-stone-50 transition-colors shadow-xs"
          >
            {language === 'da' ? 'Læs om vores priser →' : 'Read about our prices →'}
          </Link>
        </div>

        {/* Mobile Filter Toggle Button */}
        <div className="lg:hidden mb-6">
          <button 
            onClick={() => setShowMobileFilters(true)}
            className="w-full flex items-center justify-center gap-2 bg-white border border-stone-200 py-3 px-4 rounded-xl font-bold text-emerald-900 shadow-sm hover:bg-stone-50 transition-colors"
          >
            <SlidersHorizontal size={18} />
            {language === 'da' ? 'Vis søgning & filtre' : 'Show Search & Filters'} {isAnyFilterActive && <span className="w-2.5 h-2.5 bg-emerald-600 rounded-full"></span>}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Desktop Sidebar / Left Column */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white p-6 rounded-3xl shadow-xl border border-emerald-50 sticky top-24 space-y-8">
              <div>
                <p className="text-lg font-serif font-bold text-emerald-900 mb-4 flex items-center gap-2">
                  <Search size={18} className="text-[#99A18C]" />
                  {language === 'da' ? 'Søg & find' : 'Search & Find'}
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    type="text" 
                    placeholder={language === 'da' ? "Søg hotel, land, lift..." : "Search hotel, country, lift..."}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-stone-100 pt-6">
                <button 
                  onClick={() => setIsDestinationsOpen(!isDestinationsOpen)}
                  className="w-full flex items-center justify-between text-left group/btn"
                >
                  <p className="text-lg font-serif font-bold text-emerald-900 flex items-center gap-2">
                    <MapPin size={18} className="text-[#99A18C]" />
                    {language === 'da' ? 'Vælg rejsemål' : 'Select Destination'}
                  </p>
                  <span className="text-stone-400 group-hover:text-emerald-900 transition-colors">
                    {isDestinationsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </span>
                </button>
                
                <AnimatePresence initial={false}>
                  {isDestinationsOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden mt-4 space-y-2"
                    >
                      <button
                        onClick={() => setSelectedDestination('all')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${selectedDestination === 'all' ? 'bg-emerald-50 text-emerald-900 font-bold' : 'text-slate-600 hover:bg-stone-50'}`}
                      >
                        <span>{language === 'da' ? 'Alle rejsemål' : 'All Destinations'}</span>
                        <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                          {hotels.length}
                        </span>
                      </button>
                      {destinations.map(rawD => {
                        const d = translateObject(rawD);
                        const count = hotels.filter(h => h.destinationId === d.id).length;
                        return (
                          <button
                            key={d.id}
                            onClick={() => setSelectedDestination(d.id)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${selectedDestination === d.id ? 'bg-emerald-50 text-emerald-900 font-bold' : 'text-slate-600 hover:bg-stone-50'}`}
                          >
                            <span>{d.title}</span>
                            <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="border-t border-stone-100 pt-6">
                <button 
                  onClick={() => setIsFeaturesOpen(!isFeaturesOpen)}
                  className="w-full flex items-center justify-between text-left group/btn"
                >
                  <p className="text-lg font-serif font-bold text-emerald-900 flex items-center gap-2">
                    <Filter size={18} className="text-[#99A18C]" />
                    {language === 'da' ? 'Faciliteter' : 'Amenities'}
                  </p>
                  <span className="text-stone-400 group-hover:text-emerald-900 transition-colors">
                    {isFeaturesOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isFeaturesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden mt-4 space-y-3"
                    >
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          className="sr-only"
                          checked={filterPoolLift}
                          onChange={(e) => setFilterPoolLift(e.target.checked)}
                        />
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterPoolLift ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300 group-hover:border-emerald-800'}`}>
                          {filterPoolLift && <Check size={14} />}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">Pool lift</span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          className="sr-only"
                          checked={filterWellness}
                          onChange={(e) => setFilterWellness(e.target.checked)}
                        />
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterWellness ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300 group-hover:border-emerald-800'}`}>
                          {filterWellness && <Check size={14} />}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">Wellness & Spa</span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          className="sr-only"
                          checked={filterBoernevenlig}
                          onChange={(e) => setFilterBoernevenlig(e.target.checked)}
                        />
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterBoernevenlig ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300 group-hover:border-emerald-800'}`}>
                          {filterBoernevenlig && <Check size={14} />}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{language === 'da' ? 'Børnevenligt' : 'Child friendly'}</span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          className="sr-only"
                          checked={filterLejligheder}
                          onChange={(e) => setFilterLejligheder(e.target.checked)}
                        />
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterLejligheder ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300 group-hover:border-emerald-800'}`}>
                          {filterLejligheder && <Check size={14} />}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{language === 'da' ? 'Lejligheder' : 'Apartments'}</span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          className="sr-only"
                          checked={filterVoksenhotel}
                          onChange={(e) => setFilterVoksenhotel(e.target.checked)}
                        />
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterVoksenhotel ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300 group-hover:border-emerald-800'}`}>
                          {filterVoksenhotel && <Check size={14} />}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{language === 'da' ? 'Voksenhotel' : 'Adults only'}</span>
                      </label>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isAnyFilterActive && (
                <button 
                  onClick={resetFilters}
                  className="w-full py-2.5 bg-stone-100 text-stone-600 hover:bg-stone-200 rounded-xl text-sm font-bold transition-colors"
                >
                  {language === 'da' ? 'Nulstil filtre' : 'Reset Filters'}
                </button>
              )}

              <div className="bg-emerald-50/50 p-4 rounded-2xl text-center border border-emerald-100">
                <p className="text-xs text-emerald-800 font-semibold">
                  {language === 'da' 
                    ? `Viser ${filteredHotels.length} af ${hotels.length} hoteller`
                    : `Showing ${filteredHotels.length} of ${hotels.length} hotels`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Hotel List / Right Column */}
          <div className="lg:col-span-3 space-y-8">
            {filteredHotels.length > 0 ? (
              filteredHotels.map((hotel, index) => {
                const dest = destinations.find(d => d.id === hotel.destinationId);
                return (
                  <HotelCard 
                    key={hotel.id} 
                    hotel={hotel} 
                    index={index} 
                    dest={dest} 
                  />
                );
              })
            ) : (
              <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-stone-300">
                <Hotel size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-xl text-stone-500 font-serif italic">
                  {language === 'da' ? 'Vi kunne ikke finde nogen hoteller, der matcher din søgning.' : 'We could not find any hotels matching your search.'}
                </p>
                <button 
                  onClick={resetFilters}
                  className="mt-4 text-emerald-700 font-bold underline"
                >
                  {language === 'da' ? 'Nulstil filtre' : 'Reset Filters'}
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Extra Information Section */}
        <div className="mt-20 bg-stone-50/80 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-stone-200/60 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Left Column: What characterizes an accessible hotel */}
            <div className="space-y-6">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-emerald-900 italic">
                {language === 'da' ? 'Hvad kendetegner et handicapvenligt hotel?' : 'What Characterizes an Accessible Hotel?'}
              </h2>
              <p className="text-stone-700 leading-relaxed">
                {language === 'da' 
                  ? 'Et handicapvenligt hotel handler om meget mere end en rampe ved indgangen. De små detaljer kan gøre en stor forskel for, om opholdet bliver afslappende eller fyldt med udfordringer.'
                  : 'An accessible hotel is about much more than a ramp at the entrance. The small details can make a huge difference in whether the stay is relaxing or filled with challenges.'
                }
              </p>
              <p className="text-stone-800 font-semibold">
                {language === 'da' ? 'Når vi vurderer handicapvenlige hoteller, ser vi blandt andet på:' : 'When we evaluate accessible hotels, we look at:'}
              </p>
              <ul className="space-y-3">
                {(language === 'da' ? [
                  "Adgang fra indgang og reception uden unødige trin",
                  "Elevatorer med tilstrækkelig plads",
                  "Handicapvenlige værelser med god vendeplads",
                  "Badeværelser med støttegreb og praktiske løsninger",
                  "Tilgængelige restauranter og fællesområder",
                  "Afstande til strand, byliv og lokale oplevelser",
                  "Muligheder for transport og transfer"
                ] : [
                  "Step-free access from the entrance and reception",
                  "Elevators with sufficient space",
                  "Wheelchair friendly rooms with good turning space",
                  "Bathrooms with grab bars and practical solutions",
                  "Accessible restaurants and common areas",
                  "Distances to beach, city life, and local experiences",
                  "Options for transport and transfers"
                ]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-700">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mt-0.5">
                      <Check size={14} className="stroke-[3]" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-stone-700 leading-relaxed pt-2">
                {language === 'da'
                  ? 'Vi ved, at standarder for tilgængelighed varierer fra land til land. Derfor hjælper vi med at skabe et realistisk billede af forholdene, inden du bestiller.'
                  : 'We know that accessibility standards vary from country to country. That is why we help create a realistic picture of the conditions before you book.'
                }
              </p>
            </div>

            {/* Right Column: Popular Destinations & Why Alora */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-xl md:text-2xl font-serif font-bold text-emerald-900 italic">
                  {language === 'da' ? 'Handicapvenlige hoteller på populære destinationer' : 'Accessible Hotels in Popular Destinations'}
                </h2>
                <p className="text-stone-700 leading-relaxed">
                  {language === 'da'
                    ? 'Mange af vores gæster vælger handicapvenlige hoteller på Mallorca og Kreta, hvor klimaet, omgivelserne og hotelområderne gør destinationerne populære blandt rejsende med nedsat mobilitet.'
                    : 'Many of our guests choose accessible hotels in Mallorca and Crete, where the climate, surroundings, and hotel areas make these destinations popular among travelers with reduced mobility.'
                  }
                </p>
                <p className="text-stone-700 leading-relaxed">
                  {language === 'da'
                    ? 'Andre foretrækker kultur og storbyoplevelser i Paris eller Venedig, mens Østrig er et oplagt valg for dig, der ønsker naturoplevelser og afslappende omgivelser.'
                    : 'Others prefer culture and city breaks in Paris or Venice, while Austria is an obvious choice for those who want nature experiences and relaxing surroundings.'
                  }
                </p>
                <p className="text-stone-700 leading-relaxed">
                  {language === 'da'
                    ? 'Uanset om du leder efter et handicapvenligt hotel eller planlægger en handicapvenlig ferie, hjælper vi med at afklare de praktiske forhold, så du får et ophold, der passer til dit mobilitetsniveau og dine ønsker.'
                    : 'Whether you are looking for an accessible hotel or planning an accessible holiday, we help clarify the practical conditions so you get a stay that fits your mobility level and wishes.'
                  }
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl md:text-2xl font-serif font-bold text-emerald-900 italic">
                  {language === 'da' 
                    ? 'Derfor vælger rejsende Alora Rejser, når de søger handicapvenlige hoteller'
                    : 'Why Travelers Choose Alora Rejser When Searching for Accessible Hotels'
                  }
                </h2>
                <p className="text-stone-700 leading-relaxed">
                  {language === 'da'
                    ? 'Alora Rejser blev skabt med ønsket om at gøre det nemmere at finde handicapvenlige hoteller og tilgængelige rejseoplevelser for mennesker med handicap og nedsat mobilitet.'
                    : 'Alora Rejser was created with the desire to make it easier to find accessible hotels and barrier-free travel experiences for people with disabilities and reduced mobility.'
                  }
                </p>
                <p className="text-stone-700 leading-relaxed">
                  {language === 'da'
                    ? 'Vi ved, at ingen rejsende er ens. Derfor tager vi os tid til at forstå dine behov, så vi kan hjælpe med at finde handicapvenlige hoteller og destinationer, der passer bedst til dig.'
                    : 'We know that no two travelers are alike. That is why we take the time to understand your needs, so we can help find the accessible hotels and destinations that suit you best.'
                  }
                </p>
                <p className="text-stone-700 leading-relaxed">
                  {language === 'da'
                    ? 'Selvom vi har base i Silkeborg, hjælper vi rejsende i hele Danmark med personlig rådgivning og planlægning. Vi hjælper dig med at afklare de praktiske detaljer, så du kan vælge et handicapvenligt hotel med større tryghed og bedre overblik.'
                    : 'Although we are based in Silkeborg, we help travelers throughout Denmark with personal advice and planning. We help you clarify the practical details so you can choose an accessible hotel with greater confidence and a better overview.'
                  }
                </p>
              </div>
            </div>

          </div>

        </div>

        {/* Bottom Call to Action Box */}
        <div className="mt-16 bg-stone-50 border border-stone-200/80 rounded-[2rem] p-8 md:p-12 shadow-sm flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-4 max-w-2xl">
            <h2 className="text-2xl font-serif font-bold text-emerald-900 italic">
              {language === 'da' ? 'Find handicapvenlige hoteller til din næste ferie' : 'Find Accessible Hotels for Your Next Holiday'}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {language === 'da'
                ? 'Et handicapvenligt hotel skal ikke føles som et kompromis. Det skal give dig mulighed for at slappe af og nyde opholdet med tryghed omkring tilgængelighed, komfort og de praktiske detaljer.'
                : 'An accessible hotel should not feel like a compromise. It should allow you to relax and enjoy your stay with peace of mind regarding accessibility, comfort, and practical details.'
              }
            </p>
            <p className="text-stone-700 leading-relaxed">
              {language === 'da'
                ? 'Kontakt Alora Rejser, hvis du ønsker personlig rådgivning om handicapvenlige hoteller, tilgængelige værelser og destinationer i Europa.'
                : 'Contact Alora Rejser if you want personal advice on accessible hotels, wheelchair friendly rooms, and destinations in Europe.'
              }
            </p>
            <p className="text-stone-600 italic font-medium">
              {language === 'da'
                ? 'Vi glæder os til at hjælpe dig med at finde det handicapvenlige hotel, der passer bedst til dine behov og ønsker.'
                : 'We look forward to helping you find the accessible hotel that best suits your needs and wishes.'
              }
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm space-y-4 min-w-[280px] w-full md:w-auto">
            <p className="text-sm font-bold text-stone-400 uppercase tracking-wider">
              {language === 'da' ? 'Direkte kontakt' : 'Direct Contact'}
            </p>
            <div className="space-y-3">
              <a href="tel:+4540500780" className="flex items-center gap-3 text-emerald-900 hover:text-emerald-700 font-bold transition-colors">
                <span className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-800">
                  <i className="fa-solid fa-phone text-sm"></i>
                </span>
                <span>+45 40 50 07 80</span>
              </a>
              <a href="mailto:info@alorarejser.dk" className="flex items-center gap-3 text-emerald-900 hover:text-emerald-700 font-bold transition-colors">
                <span className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-800">
                  <i className="fa-solid fa-envelope text-sm"></i>
                </span>
                <span>info@alorarejser.dk</span>
              </a>
            </div>
            <div className="pt-2 border-t border-stone-100">
              <Link to={getLocalizedPath('contact', language)} className="w-full py-3 bg-emerald-900 hover:bg-emerald-800 text-white font-bold rounded-xl text-center block text-sm transition-colors shadow-md">
                {language === 'da' ? 'Kontakt os i dag' : 'Contact Us Today'}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer Overlay */}
      <AnimatePresence>
        {showMobileFilters && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-black z-50 lg:hidden"
            />

            {/* Drawer */}
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed inset-y-0 right-0 w-full max-w-md bg-white z-50 shadow-2xl p-6 overflow-y-auto lg:hidden flex flex-col justify-between"
            >
              <div className="space-y-8">
                <div className="flex items-center justify-between border-b border-stone-100 pb-4">
                  <h2 className="text-2xl font-serif font-bold text-emerald-900">
                    {language === 'da' ? 'Søg & filtrer' : 'Search & Filter'}
                  </h2>
                  <button 
                    onClick={() => setShowMobileFilters(false)}
                    className="p-2 text-stone-500 hover:text-stone-800 rounded-full hover:bg-stone-100"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Search */}
                <div>
                  <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider mb-3">
                    {language === 'da' ? 'Søg' : 'Search'}
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                      type="text" 
                      placeholder={language === 'da' ? "Søg hotel, land, lift..." : "Search hotel, country, lift..."}
                      className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Destination */}
                <div>
                  <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider mb-3">
                    {language === 'da' ? 'Vælg rejsemål' : 'Select Destination'}
                  </h3>
                  <select 
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm appearance-none"
                    value={selectedDestination}
                    onChange={(e) => setSelectedDestination(e.target.value)}
                  >
                    <option value="all">{language === 'da' ? 'Alle rejsemål' : 'All Destinations'}</option>
                    {destinations.map(rawD => {
                      const d = translateObject(rawD);
                      return (
                        <option key={d.id} value={d.id}>{d.title}</option>
                      );
                    })}
                  </select>
                </div>

                {/* Facilities Checkboxes */}
                <div>
                  <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider mb-3">
                    {language === 'da' ? 'Faciliteter' : 'Amenities'}
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only"
                        checked={filterPoolLift}
                        onChange={(e) => setFilterPoolLift(e.target.checked)}
                      />
                      <div className={`w-6 h-6 rounded border flex items-center justify-center transition-all ${filterPoolLift ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300'}`}>
                        {filterPoolLift && <Check size={16} />}
                      </div>
                      <span className="text-slate-700 font-medium">Pool lift</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only"
                        checked={filterWellness}
                        onChange={(e) => setFilterWellness(e.target.checked)}
                      />
                      <div className={`w-6 h-6 rounded border flex items-center justify-center transition-all ${filterWellness ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300'}`}>
                        {filterWellness && <Check size={16} />}
                      </div>
                      <span className="text-slate-700 font-medium">Wellness & Spa</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only"
                        checked={filterBoernevenlig}
                        onChange={(e) => setFilterBoernevenlig(e.target.checked)}
                      />
                      <div className={`w-6 h-6 rounded border flex items-center justify-center transition-all ${filterBoernevenlig ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300'}`}>
                        {filterBoernevenlig && <Check size={16} />}
                      </div>
                      <span className="text-slate-700 font-medium">{language === 'da' ? 'Børnevenligt' : 'Child friendly'}</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only"
                        checked={filterLejligheder}
                        onChange={(e) => setFilterLejligheder(e.target.checked)}
                      />
                      <div className={`w-6 h-6 rounded border flex items-center justify-center transition-all ${filterLejligheder ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300'}`}>
                        {filterLejligheder && <Check size={16} />}
                      </div>
                      <span className="text-slate-700 font-medium">{language === 'da' ? 'Lejligheder' : 'Apartments'}</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only"
                        checked={filterVoksenhotel}
                        onChange={(e) => setFilterVoksenhotel(e.target.checked)}
                      />
                      <div className={`w-6 h-6 rounded border flex items-center justify-center transition-all ${filterVoksenhotel ? 'bg-emerald-800 border-emerald-800 text-white' : 'border-stone-300'}`}>
                        {filterVoksenhotel && <Check size={16} />}
                      </div>
                      <span className="text-slate-700 font-medium">{language === 'da' ? 'Voksenhotel' : 'Adults only'}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="border-t border-stone-100 pt-6 mt-8 space-y-3">
                <button 
                  onClick={() => setShowMobileFilters(false)}
                  className="w-full py-4 bg-emerald-900 text-white font-bold rounded-xl text-center shadow-lg"
                >
                  {language === 'da' ? `Vis ${filteredHotels.length} hoteller` : `Show ${filteredHotels.length} Hotels`}
                </button>
                {isAnyFilterActive && (
                  <button 
                    onClick={() => { resetFilters(); setShowMobileFilters(false); }}
                    className="w-full py-3 bg-stone-100 text-stone-600 font-bold rounded-xl text-center"
                  >
                    {language === 'da' ? 'Nulstil alle filtre' : 'Reset All Filters'}
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AllHotelsPage;
